import React from "react";
import { colors } from "../../data/colors";
import { markings } from "../../data/markings";
import { ElementInspector } from "./ElementInspector";
import { LayerList } from "./LayerList";
import { describe } from "../design/DesignElementView";
import { formatEuro } from "../../utils/pricing";
import type { Configurator } from "../../hooks/useConfigurator";
import type { DesignElement } from "../../types/configurator";
interface StudioPanelProps {
  config: Configurator;
  activeZoneId: string;
  selected: DesignElement | null;
  onZoneChange: (zoneId: string) => void;
  onSelect: (el: DesignElement | null) => void;
  onSelectId: (id: string | null) => void;
}
export function StudioPanel({
  config,
  activeZoneId,
  selected,
  onZoneChange,
  onSelect,
  onSelectId
}: StudioPanelProps) {
  const {
    product,
    color,
    category,
    marking,
    mockup,
    design,
    state
  } = config;
  if (!product || !mockup || !category) return null;
  const available = colors.filter(c => product.colors.includes(c.id));
  const techniques = markings.filter(m => category.markings.includes(m.id));
  return <div className="border border-line" data-id="element-322" data-mp="18zdk5js6yowu">
      <Section title="Couleur du produit" aside={color?.name} data-id="element-323" data-mp="18xiyu4d0cus8">
        <div className="flex flex-wrap gap-2" data-id="element-324" data-mp="13zhhpoz575je">
          {available.map(c => {
          const isSel = color?.id === c.id;
          return <button key={c.id} type="button" aria-label={c.name} title={c.name} aria-pressed={isSel} onClick={() => config.update("colorId", c.id)} className={`h-9 w-9 rounded-full border border-ink/30 transition-shadow duration-150 ease-out ${isSel ? "ring-2 ring-ink ring-offset-2" : "hover:ring-1 hover:ring-ink hover:ring-offset-2"}`} style={{
            backgroundColor: c.hex
          }} data-id="element-325" data-mp="z2qy4ro4prt8" />;
        })}
        </div>
      </Section>

      <Section title="Emplacement" data-id="element-326" data-mp="78yrmex97knb">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Emplacement" data-id="element-327" data-mp="7c4gpu5vumj1">
          {mockup.zones.map(z => {
          const count = design.elements.filter(e => e.zoneId === z.id).length;
          const isActive = z.id === activeZoneId;
          return <button key={z.id} type="button" role="radio" aria-checked={isActive} onClick={() => onZoneChange(z.id)} className={`flex items-center gap-2 rounded-full border border-ink px-4 py-2 text-sm font-bold transition-colors duration-150 ease-out ${isActive ? "bg-ink text-paper" : "hover:bg-mist"}`} data-id="element-328" data-mp="3v082jzhg5agf">
                {z.name}
                {count > 0 && <span className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[11px] ${isActive ? "bg-mint text-ink" : "bg-ink text-paper"}`} data-id="element-329" data-mp="3h4e6ih1rufkq">
                    {count}
                  </span>}
              </button>;
        })}
        </div>
      </Section>

      <Section title="Technique" data-id="element-330" data-mp="2jlflx7y7abku">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Technique de marquage" data-id="element-331" data-mp="2jmh5k0ta86e6">
          {techniques.map(m => <button key={m.id} type="button" role="radio" aria-checked={marking?.id === m.id} onClick={() => config.selectMarking(m.id)} className={`border border-ink px-4 py-2 text-sm font-bold transition-colors duration-150 ease-out ${marking?.id === m.id ? "bg-mint" : "hover:bg-mist"}`} data-id="element-332" data-mp="2xikmwtj24k98">
              {m.name}
            </button>)}
        </div>
        {marking && <p className="mt-3 text-sm text-muted" data-id="element-333" data-mp="3q9m7xb9fla0o">
            {marking.description} Calage {formatEuro(marking.setupFee)}
            {marking.setupPerColor ? " par couleur" : ""} et par emplacement.
          </p>}
      </Section>

      {selected && <Section title={describe(selected)} highlight data-id="element-334" data-mp="96zth4bs04q1">
          <ElementInspector el={selected} design={design} marking={marking} logoColors={state.logoColors} onLogoColors={n => config.update("logoColors", n)} onSelect={onSelectId} data-id="element-335" data-mp="d8bojpdf5i5m" />
        </Section>}

      <Section title={`Éléments (${design.elements.length})`} last data-id="element-336" data-mp="3me9x29bdwawi">
        <LayerList elements={design.elements} mockup={mockup} selectedId={selected?.id ?? null} onSelect={onSelect} onRemove={id => {
        design.remove(id);
        if (selected?.id === id) onSelect(null);
      }} data-id="element-337" data-mp="3mg4jjs2b0b2p" />
      </Section>
    </div>;
}
interface SectionProps {
  title: string;
  aside?: string;
  highlight?: boolean;
  last?: boolean;
  children: React.ReactNode;
}
function Section({
  title,
  aside,
  highlight,
  last,
  children
}: SectionProps) {
  return <section className={`p-5 md:p-6 ${last ? "" : "border-b border-line"} ${highlight ? "bg-mist" : ""}`} data-id="element-338" data-mp="305mfvceya2u6">
      <div className="mb-3 flex items-baseline justify-between gap-3" data-id="element-339" data-mp="2v7b42w53hi9b">
        <h2 className="truncate text-sm font-bold" data-id="element-340" data-mp="2qf1h9oza0yb9">{title}</h2>
        {aside && <span className="text-sm text-muted" data-id="element-341" data-mp="2qd6wcao09676">{aside}</span>}
      </div>
      {children}
    </section>;
}